import { useState, useEffect } from 'react'
import AdminLayout from '../components/AdminLayout'
import { supabase } from '../services/supabase'

interface DiscountCode {
  id: string
  code: string
  discount_type: 'percentage' | 'fixed'
  discount_value: number
  expiration_date: string | null
  minimum_order_amount: number | null
  created_at: string
}

export default function AdminDiscountCodes() {
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Form state
  const [code, setCode] = useState('')
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage')
  const [discountValue, setDiscountValue] = useState('')
  const [expirationDate, setExpirationDate] = useState('')
  const [minimumOrderAmount, setMinimumOrderAmount] = useState('')

  // Fetch discount codes
  useEffect(() => {
    fetchDiscountCodes()
  }, [])

  const fetchDiscountCodes = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setDiscountCodes(data || [])
    } catch (error) {
      console.error('Error fetching discount codes:', error)
      setMessage({ type: 'error', text: 'Failed to load discount codes' })
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setCode('')
    setDiscountType('percentage')
    setDiscountValue('')
    setExpirationDate('')
    setMinimumOrderAmount('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    // Validation
    if (!code.trim()) {
      setMessage({ type: 'error', text: 'Code is required' })
      return
    }

    const value = parseFloat(discountValue)
    if (isNaN(value) || value <= 0) {
      setMessage({ type: 'error', text: 'Discount value must be greater than 0' })
      return
    }

    if (discountType === 'percentage' && value > 100) {
      setMessage({ type: 'error', text: 'Percentage discount cannot exceed 100%' })
      return
    }

    const minAmount = minimumOrderAmount ? parseFloat(minimumOrderAmount) : null
    if (minAmount !== null && (isNaN(minAmount) || minAmount < 0)) {
      setMessage({ type: 'error', text: 'Minimum order amount must be a positive number' })
      return
    }

    setSubmitting(true)

    try {
      const { error } = await supabase
        .from('discount_codes')
        .insert({
          code: code.trim().toUpperCase(),
          discount_type: discountType,
          discount_value: value,
          expiration_date: expirationDate || null,
          minimum_order_amount: minAmount
        })

      if (error) {
        if (error.code === '23505') {
          setMessage({ type: 'error', text: 'A discount code with this code already exists' })
        } else {
          throw error
        }
        return
      }

      setMessage({ type: 'success', text: 'Discount code created successfully!' })
      resetForm()
      fetchDiscountCodes()

      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Error creating discount code:', error)
      setMessage({ type: 'error', text: 'Failed to create discount code' })
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const isExpired = (expirationDate: string | null) => {
    if (!expirationDate) return false
    return new Date(expirationDate) < new Date()
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Discount Codes</h1>
          <p className="text-gray-500 text-sm">Create and manage discount codes for your store</p>
        </div>

        {/* Message Alert */}
        {message && (
          <div className={`mb-5 p-4 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Create Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Create New Code</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Code */}
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="SAVE20"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase"
                  />
                </div>

                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setDiscountType('percentage')}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                        discountType === 'percentage'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Percentage (%)
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiscountType('fixed')}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                        discountType === 'fixed'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Fixed ($)
                    </button>
                  </div>
                </div>

                {/* Discount Value */}
                <div>
                  <label htmlFor="discountValue" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {discountType === 'percentage' ? 'Percentage Off' : 'Amount Off ($)'} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="discountValue"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      placeholder={discountType === 'percentage' ? '20' : '10.00'}
                      min="0"
                      step={discountType === 'percentage' ? '1' : '0.01'}
                      max={discountType === 'percentage' ? '100' : undefined}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      {discountType === 'percentage' ? '%' : '$'}
                    </span>
                  </div>
                </div>

                {/* Expiration Date */}
                <div>
                  <label htmlFor="expirationDate" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Expiration Date <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="date"
                    id="expirationDate"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Minimum Order Amount */}
                <div>
                  <label htmlFor="minimumOrderAmount" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Minimum Order Amount <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="number"
                      id="minimumOrderAmount"
                      value={minimumOrderAmount}
                      onChange={(e) => setMinimumOrderAmount(e.target.value)}
                      placeholder="50.00"
                      min="0"
                      step="0.01"
                      className="w-full pl-7 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                    submitting
                      ? 'bg-purple-400 text-white cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Creating...
                    </span>
                  ) : (
                    'Create Discount Code'
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Discount Codes List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">
                  Existing Codes ({discountCodes.length})
                </h2>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
                  <p className="mt-3 text-gray-500 text-sm">Loading discount codes...</p>
                </div>
              ) : discountCodes.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-sm">No discount codes yet</p>
                  <p className="text-gray-400 text-xs mt-1">Create your first discount code using the form</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Discount</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Min. Order</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Expires</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {discountCodes.map((discountCode) => (
                        <tr key={discountCode.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded">
                              {discountCode.code}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {discountCode.discount_type === 'percentage'
                              ? `${discountCode.discount_value}% off`
                              : `$${discountCode.discount_value.toFixed(2)} off`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {discountCode.minimum_order_amount
                              ? `$${discountCode.minimum_order_amount.toFixed(2)}`
                              : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {discountCode.expiration_date
                              ? formatDate(discountCode.expiration_date)
                              : 'Never'}
                          </td>
                          <td className="px-4 py-3">
                            {isExpired(discountCode.expiration_date) ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                Expired
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Active
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
